import React, { useState, useEffect, useRef, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { makeApiCall } from '../utils/customerUtils'
import { SearchIcon, XIcon, ClockIcon, UserIcon, BriefcaseIcon, PhoneIcon, MessageSquareIcon } from './Icons'

interface SearchResult {
  id: string
  type: 'customer' | 'deal' | 'lead' | 'comment' | 'phone' | 'task'
  title: string
  subtitle: string
  description?: string
  url: string
  icon: React.ComponentType<{ className?: string }>
  metadata?: {
    status?: string
    statusColor?: string
    date?: string
    amount?: string
  }
}

interface GlobalSearchProps {
  className?: string
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ className = '' }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [recentSearches, setRecentSearches] = useState<string[]>([])
  
  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 })
  const navigate = useNavigate()

  // Update dropdown position when search opens or window resizes
  useEffect(() => {
    const updatePosition = () => {
      if (searchRef.current && isOpen) {
        const rect = searchRef.current.getBoundingClientRect()
        setDropdownPosition({
          top: rect.bottom + 4, // 4px gap
          left: rect.left,
          width: rect.width
        })
      }
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition, true)
    
    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition, true)
    }
  }, [isOpen])

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches')
    if (saved) {
      try {
        setRecentSearches(JSON.parse(saved))
      } catch (error) {
        console.error('Failed to load recent searches:', error)
      }
    }
  }, [])

  // Save recent searches to localStorage
  const saveRecentSearch = useCallback((searchTerm: string) => {
    if (!searchTerm.trim()) return
    
    const updated = [searchTerm, ...recentSearches.filter(s => s !== searchTerm)].slice(0, 5)
    setRecentSearches(updated)
    localStorage.setItem('recentSearches', JSON.stringify(updated))
  }, [recentSearches])

  // Debounced search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim()) {
      setResults([])
      return
    }

    console.log(`Performing search for: "${searchQuery}"`)
    setIsLoading(true)
    try {
      const response = await makeApiCall(`/api/search/global?q=${encodeURIComponent(searchQuery)}&limit=10`)
      if (response.ok) {
        const data = await response.json()
        console.log(`Search results for "${searchQuery}":`, data.results)
        setResults(data.results || [])
      } else {
        console.error('Search failed:', response.statusText)
        setResults([])
      }
    } catch (error) {
      console.error('Search error:', error)
      setResults([])
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Debounce search requests - Minimal delay for instant feel
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch(query)
    }, 100)

    return () => clearTimeout(timeoutId)
  }, [query, performSearch])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, -1))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleResultClick(results[selectedIndex])
        } else if (query.trim()) {
          // Navigate to search results page
          navigate(`/dashboard/search?q=${encodeURIComponent(query)}`)
          setIsOpen(false)
          setQuery('')
        }
        break
      case 'Escape':
        setIsOpen(false)
        setQuery('')
        inputRef.current?.blur()
        break
    }
  }

  // Handle result click
  const handleResultClick = (result: SearchResult, event?: React.MouseEvent) => {
    // Stop event propagation to prevent backdrop from closing search before navigation
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }
    
    console.log('Search result clicked:', result)
    console.log('Navigating to:', result.url)
    
    saveRecentSearch(query)
    
    // Close the search UI
    setIsOpen(false)
    setQuery('')
    inputRef.current?.blur()
    
    // Use setTimeout to ensure navigation happens after state updates
    setTimeout(() => {
      console.log('Executing navigation to:', result.url)
      navigate(result.url)
    }, 0)
  }

  // Handle recent search click
  const handleRecentSearchClick = (searchTerm: string) => {
    setQuery(searchTerm)
    inputRef.current?.focus()
  }

  // Clear search
  const clearSearch = () => {
    setQuery('')
    setResults([])
    inputRef.current?.focus()
  }

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      const isInsideSearch = searchRef.current?.contains(target)
      const isInsideDropdown = dropdownRef.current?.contains(target)
      
      // Only close if clicking outside both the search input AND the dropdown
      if (!isInsideSearch && !isInsideDropdown) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Global keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        inputRef.current?.focus()
        setIsOpen(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Focus input when search opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isOpen])

  const getResultIcon = (type: string) => {
    switch (type) {
      case 'customer': return UserIcon
      case 'deal': return BriefcaseIcon
      case 'lead': return PhoneIcon
      case 'comment': return MessageSquareIcon
      case 'phone': return PhoneIcon
      case 'task': return ClockIcon
      default: return SearchIcon
    }
  }

  const formatResultTitle = (result: SearchResult) => {
    if (result.type === 'comment') {
      return t('search.commentIn', { title: result.title })
    }
    return result.title
  }

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <SearchIcon className="h-4 w-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('dashboard.search.placeholder')}
          className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent transition-all duration-200"
        />
        {query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XIcon className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {/* Search Results Dropdown - Rendered via Portal to escape stacking context */}
      {isOpen && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/5"
            style={{ zIndex: 2147483646 }}
            onClick={() => setIsOpen(false)} 
          />
          {/* Dropdown */}
          <div 
            ref={dropdownRef}
            className="fixed bg-white dark:bg-gray-800 rounded-lg shadow-2xl border border-gray-200 dark:border-gray-700 max-h-96 overflow-y-auto"
            style={{ 
              zIndex: 2147483647,
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width
            }}
          >
          {query.trim() ? (
            <>
              {/* Search Results - Show loading overlay instead of replacing content */}
              {isLoading && results.length === 0 ? (
                <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
                  <p className="mt-2 text-sm">{t('search.searching')}</p>
                </div>
              ) : results.length > 0 ? (
                <div className="py-1">
                  {results.map((result, index) => {
                    const IconComponent = getResultIcon(result.type)
                    return (
                      <button
                        key={`${result.type}-${result.id}`}
                        onClick={(e) => handleResultClick(result, e)}
                        className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 cursor-pointer ${
                          index === selectedIndex ? 'bg-gray-50 dark:bg-gray-700' : ''
                        }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className="flex-shrink-0 mt-0.5">
                            <IconComponent className="h-4 w-4 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {formatResultTitle(result)}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                              {result.subtitle}
                            </p>
                            {result.description && (
                              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 line-clamp-2">
                                {result.description}
                              </p>
                            )}
                            {result.metadata && (
                              <div className="flex items-center space-x-2 mt-1">
                                {result.metadata.status && (
                                  <span 
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                                    style={{ 
                                      backgroundColor: result.metadata.statusColor + '20',
                                      color: result.metadata.statusColor 
                                    }}
                                  >
                                    {result.metadata.status}
                                  </span>
                                )}
                                {result.metadata.amount && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {result.metadata.amount}
                                  </span>
                                )}
                                {result.metadata.date && (
                                  <span className="text-xs text-gray-500 dark:text-gray-400">
                                    {result.metadata.date}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                  
                  {/* View All Results */}
                  <div className="border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => {
                        navigate(`/dashboard/search?q=${encodeURIComponent(query)}`)
                        setIsOpen(false)
                        setQuery('')
                      }}
                      className="w-full px-4 py-2 text-sm text-gray-900 dark:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center justify-center space-x-2"
                    >
                      <SearchIcon className="h-4 w-4" />
                      <span>{t('search.viewAllResults', { count: results.length, query })}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 text-center text-gray-500 dark:text-gray-400">
                  <SearchIcon className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm">{t('search.noResults', { query })}</p>
                  <p className="text-xs mt-1">{t('search.tryDifferent')}</p>
                </div>
              )}
            </>
          ) : (
            /* Recent Searches */
            <div className="py-1">
              {recentSearches.length > 0 ? (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('search.recentSearches')}
                  </div>
                  {recentSearches.map((searchTerm, index) => (
                    <button
                      key={index}
                      onClick={() => handleRecentSearchClick(searchTerm)}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 flex items-center space-x-2"
                    >
                      <ClockIcon className="h-4 w-4 text-gray-400" />
                      <span>{searchTerm}</span>
                    </button>
                  ))}
                </>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  <SearchIcon className="h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm">{t('search.startTyping')}</p>
                </div>
              )}
            </div>
          )}
          </div>
        </>,
        document.body
      )}
    </div>
  )
}
